import { Checkbox as CheckboxPrimitive } from "radix-ui";
import React from "react";

import { cn } from "~/components/ui/cn";
import { CheckIcon } from "~/components/ui/icons/check";

export type OrderCheckboxProps = {
  /**
   * Is the checkbox disabled?
   */
  disabled?: boolean;
  /**
   * Is the checkbox checked by default?
   */
  defaultChecked?: boolean;
  /**
   * Define an handler for checked-changing state
   */
  onCheckedChange?:
    | ((checked: CheckboxPrimitive.CheckedState) => void)
    | undefined;
} & React.ComponentProps<"button">;

/**
 * Main checkbox component for user interaction.
 */
export const OrderCheckbox: React.FC<OrderCheckboxProps> = ({
  id,
  disabled = false,
  defaultChecked,
  onCheckedChange,
  children,
  className,
  ...props
}) => {
  const [checked, setChecked] = React.useState<CheckboxPrimitive.CheckedState>(
    typeof defaultChecked !== "undefined" ? defaultChecked : "indeterminate",
  );

  return (
    <CheckboxPrimitive.Root
      id={id}
      className={cn(
        {
          "border-gray-900 focus:ring-2 focus:ring-gray-900/30":
            !disabled && checked === true,
          "border-gray-100 focus:ring-2 focus:ring-gray-900/30":
            !disabled && checked !== true,
          "cursor-pointer text-gray-900": !disabled,
          "cursor-not-allowed border-gray-300 text-gray-300": disabled,
          "group flex items-center space-x-2 rounded border p-2 text-sm outline-0 transition duration-300": true,
        },
        className,
      )}
      disabled={disabled}
      defaultChecked={checked === true}
      onCheckedChange={(checked: CheckboxPrimitive.CheckedState) => {
        setChecked(checked);
        onCheckedChange?.(checked);
      }}
      checked={checked}
      {...props}
    >
      <div
        className={cn(
          "flex size-[13.33px] items-center justify-center rounded-sm border",
          {
            "border-gray-900 bg-gray-900 group-focus:ring-2 group-focus:ring-gray-900/30":
              !disabled && checked === true,
            "bg-gray-300": disabled && checked === true,
            "border-[#767A7C] focus:ring-2 group-focus:ring-gray-900/30":
              !disabled && checked !== true,
            "bg-white": disabled && checked !== true,
            "cursor-pointer": !disabled,
            "cursor-not-allowed border-gray-300": disabled,
          },
        )}
      >
        <CheckboxPrimitive.Indicator>
          <CheckIcon
            className={cn("size-3", {
              "bg-gray-300": disabled,
              "opacity-0": checked !== true,
              "opacity-1 size-[0.58331rem] text-white transition duration-300": true,
            })}
          />
        </CheckboxPrimitive.Indicator>
      </div>

      <span>{children}</span>
    </CheckboxPrimitive.Root>
  );
};

OrderCheckbox.displayName = "OrderCheckbox";
